import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../../components/custom-table/custom-table";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { PaginationKeys } from "../../../enums/nycbsPharmaOrder";
import { useDrugCohort } from "../../../hooks/useDrugCohort";
import { useDrugColorMatcher } from "../../../hooks/useDrugColorMatcher";

import CmmInputViewerModalForCmmOrders from "../../../components/modals/cmmInputViewerModal/cmmInputViewerModalForCmmOrders";
import CmmOrderDeleteModal from "../../../components/modals/cmmOrderDeleteModal/cmmOrderDeleteModal";
import DrugLabelModal from "../../../components/modals/drugLabelModal/drugLabelModal";
import ReportMedicationModal from "../../../components/modals/reportMedication/reportMedication";
import ReportPrescriptionModal from "../../../components/modals/reportPrescription/reportPrescription";
import RerunConfirmModal from "../../../components/modals/rerunConfirmModal/rerunConfirmModal";
import SendToPlanConfirmModal from "../../../components/modals/sendToPlanConfirmModal/sendToPlanConfirmModal";
import { ModalId } from "../../../enums/modalId";
import { setFilter } from "../../../redux/slice/filterValuesSlice";
import {
  closeModal,
  setOpenedModalId,
} from "../../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { handleButtonWithThreeDotsClick } from "../actionHandlers/buttonWiththreDotsActionHandlers";
import AsteraOncologySanityExpandableRow from "../expandableRowChild/AsteraSanityExpandableRow/AsteraSanityExpandableRow";
import { useAsteraOncologySanityData } from "../hooks/useAsteraOncologySanityData";
import { useAsteraOncologySanityFilter } from "../hooks/useAsteraOncologySanityFilter";
import { useAsteraOncologySanityModals } from "../hooks/useAsteraOncologySanityModals";
import { useAsteraOncologySanitySearch } from "../hooks/useAsteraOncologySanitySearch";
import { AsteraOncologySanityTableHeader } from "../table/asteraOncologySanityTableData";
import { processFilterData } from "../utils/filterDataProcessor";
import {
  handleEndIndexOfTable,
  handleRowExpandChangeFromTableBody,
  navigateToForm,
} from "../utils/tableHandlers";

const AsteraOncologySanityTable = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();
  const tableAttributes = getTableDataFromContext("allAsteraOncologySanity");
  const { openedModalId, metaData } = useSelector(
    (state: RootState) => state.modalSliceNew,
  );

  const initialLimit = 120;
  const [endIndex, setEndIndex] = useState<number>(0);

  // Fetch drug cohort data
  const { drugCohort } = useDrugCohort();
  const { getDrugColor } = useDrugColorMatcher(drugCohort);

  const finalLimitToUse = useMemo(() => {
    const dataFromContext = tableAttributes.lastBatchFetched
      ? tableAttributes.lastBatchFetched
      : 0;
    const limitToFetch =
      dataFromContext === 0
        ? initialLimit
        : (dataFromContext + 1) * initialLimit;
    return limitToFetch;
  }, []);

  const {
    ordersData,
    loading,
    pageData,
    showInLineLoader,
    filters,
    uniqueDrugnames,
    uniqueFormNames,
    user,
    allAsteraOncologySanityTableData,
    tableDataForTable,
    setTableDataForTable,
  } = useAsteraOncologySanityData(getDrugColor);

  // First get valid filters
  const validFilters = useMemo(() => {
    return filters.filter(
      (filter) => filter.name === "allAsteraOncologySanity",
    );
  }, [filters]);

  const handleFetchDrugUsage = () => {
    dispatch(setOpenedModalId({ id: ModalId.DRUG_LABEL_MODAL }));
  };

  const {
    searchText,
    initialSearch,
    setBatchNumberRetreived,
    filterState,
    setFilterState,
    search,
    fetchAllOrders,
    resetAndFetchOrders,
    handleSearch,
  } = useAsteraOncologySanitySearch(
    finalLimitToUse,
    validFilters,
    processFilterData,
  );

  const { filterData, setFilterForTable } = useAsteraOncologySanityFilter(
    filters,
    uniqueDrugnames,
    uniqueFormNames,
    finalLimitToUse,
    processFilterData,
    initialSearch,
    fetchAllOrders,
    filterState,
    setFilterState,
    endIndex,
    allAsteraOncologySanityTableData,
  );

  const {
    openDeleteModal,
    setOpenDeleteModal,
    data,
    setData,
    openSendToPlanModal,
    sendToPlanModalType,
    openRerunModal,
    rerunType,
    rerunOrderData,
    handleSendToPlanModalOpen,
    handleSendToPlanConfirm,
    handleRerunModalOpen,
    handleRerunConfirm,
    onAsteraOncologySanityDeleteSuccess,
    closeDeleteModal,
    closeSendToPlanModal,
    closeRerunModal,
    openReportPrescriptionModal,
    selectedOrderIdForReportPrescription,
    handleReportPrescriptionModalOpen,
    closeReportPrescriptionModal,
    openReportMedicationModal,
    selectedOrderIdForReportMedication,
    handleReportMedicationModalOpen,
    closeReportMedicationModal,
  } = useAsteraOncologySanityModals(
    ordersData,
    user?.email ?? "",
    resetAndFetchOrders,
    searchText,
    search,
    fetchAllOrders,
  );

  const handleCloseAsteraOncologySanityInputModal = () => {
    dispatch(closeModal());
  };

  return (
    <>
      <CustomTable
        tableHeaders={AsteraOncologySanityTableHeader}
        tableData={tableDataForTable}
        itemsPerPage={10}
        pagesPerView={4}
        count={
          filterState
            ? allAsteraOncologySanityTableData?.length
            : ordersData?.length
        }
        totalCount={
          filterState
            ? allAsteraOncologySanityTableData?.length
            : pageData?.[PaginationKeys.TotalCount]
        }
        isFetching={loading}
        tableName={"allAsteraOncologySanity"}
        searchingText={handleSearch}
        endIndexOfTable={(index) =>
          handleEndIndexOfTable(
            index,
            ordersData,
            pageData,
            finalLimitToUse,
            setEndIndex,
            setBatchNumberRetreived,
            setTableDataForContext,
          )
        }
        showOtherButton={true}
        onReviewButtonClick={(data) => navigateToForm(data, navigate)}
        onRefreshButton={resetAndFetchOrders}
        onRowClick={(data) => navigateToForm(data, navigate)}
        isDisabled={loading}
        onDeleteIconClick={(data) => {
          setOpenDeleteModal(true);
          setData(data);
        }}
        showFilterButton={!loading && searchText.length <= 3}
        filterSectionsData={filterData}
        onFilterApplyClicked={(data) => {
          setFilterForTable(data);
          dispatch(setFilter(data));
        }}
        otherButtonMeta={[
          {
            text: "Fetch Drug Usage",
            onClick: handleFetchDrugUsage,
            disabled: false,
            buttonType: "secondary",
            size: "medium",
          },
        ]}
        showInLineLoader={showInLineLoader}
        showRefreshButton={true}
        expandableRowContent={(rowData, rowIndex) => (
          <AsteraOncologySanityExpandableRow rowData={rowData} />
        )}
        onRowExpansionChangeFromTableBody={(expanded, id) =>
          handleRowExpandChangeFromTableBody(
            expanded,
            id,
            tableDataForTable,
            setTableDataForTable,
          )
        }
        isExpandableRowTable={true}
        onButtonWithThreeDotsOptionClick={(optionId, rowId) =>
          handleButtonWithThreeDotsClick(
            optionId,
            rowId,
            user?.email ?? "",
            resetAndFetchOrders,
            handleSendToPlanModalOpen,
            handleRerunModalOpen,
            navigate,
            handleReportPrescriptionModalOpen,
            handleReportMedicationModalOpen,
          )
        }
      />
      {openDeleteModal && (
        <CmmOrderDeleteModal
          onClose={closeDeleteModal}
          data={data}
          onDeleteSuccess={onAsteraOncologySanityDeleteSuccess}
        />
      )}
      {openSendToPlanModal && (
        <SendToPlanConfirmModal
          onClose={closeSendToPlanModal}
          onSaveClick={handleSendToPlanConfirm}
          modalType={sendToPlanModalType}
        />
      )}
      {openRerunModal && rerunType && (
        <RerunConfirmModal
          onClose={closeRerunModal}
          onSaveClick={handleRerunConfirm}
          rerunType={rerunType}
          orderData={rerunOrderData}
        />
      )}
      {openedModalId === ModalId.CMM_INPUT_VIEWER_MODAL && (
        <CmmInputViewerModalForCmmOrders
          onClose={handleCloseAsteraOncologySanityInputModal}
          orderId={metaData?.orderId ?? ""}
        />
      )}
      {openReportPrescriptionModal && (
        <ReportPrescriptionModal
          onClose={closeReportPrescriptionModal}
          orderId={selectedOrderIdForReportPrescription ?? ""}
        />
      )}
      {openReportMedicationModal && (
        <ReportMedicationModal
          onClose={closeReportMedicationModal}
          orderId={selectedOrderIdForReportMedication ?? ""}
        />
      )}
      {openedModalId === ModalId.DRUG_LABEL_MODAL && (
        <DrugLabelModal onClose={handleCloseAsteraOncologySanityInputModal} />
      )}
    </>
  );
};

export default AsteraOncologySanityTable;
